import { z } from 'zod';
import { ProductSchema, BaseFilterSchema, SearchMetaSchema, SortOptionSchema } from '../../interfaces/schemas';
/**
 * Validates and sanitizes a single product
 * Logs warnings for invalid data and returns null for invalid products
 */
export function validateProduct(data, platformName) {
    try {
        return ProductSchema.parse(data);
    }
    catch (error) {
        if (error instanceof z.ZodError) {
            console.warn(`[${platformName}] Invalid product data:`, {
                data,
                errors: error.errors.map(e => ({
                    path: e.path.join('.'),
                    message: e.message
                }))
            });
        }
        return null;
    }
}
/**
 * Validates an array of products
 * Filters out invalid products and logs warnings
 */
export function validateProducts(data, platformName) {
    const validProducts = [];
    data.forEach((item, index) => {
        const validated = validateProduct(item, platformName);
        if (validated) {
            validProducts.push(validated);
        }
        else {
            console.warn(`[${platformName}] Skipping invalid product at index ${index}`);
        }
    });
    if (validProducts.length === 0 && data.length > 0) {
        console.error(`[${platformName}] All products failed validation!`);
    }
    return validProducts;
}
/**
 * Validates a single filter
 */
export function validateFilter(data, platformName) {
    try {
        return BaseFilterSchema.parse(data);
    }
    catch (error) {
        if (error instanceof z.ZodError) {
            console.warn(`[${platformName}] Invalid filter data:`, {
                data,
                errors: error.errors.map(e => ({
                    path: e.path.join('.'),
                    message: e.message
                }))
            });
        }
        return null;
    }
}
/**
 * Validates an array of filters
 */
export function validateFilters(data, platformName) {
    const validFilters = [];
    data.forEach((item, index) => {
        const validated = validateFilter(item, platformName);
        if (validated) {
            validFilters.push(validated);
        }
        else {
            console.warn(`[${platformName}] Skipping invalid filter at index ${index}`);
        }
    });
    return validFilters;
}
/**
 * Validates search metadata
 */
export function validateSearchMeta(data, platformName) {
    try {
        return SearchMetaSchema.parse(data);
    }
    catch (error) {
        if (error instanceof z.ZodError) {
            console.error(`[${platformName}] Invalid search meta:`, {
                data,
                errors: error.errors.map(e => ({
                    path: e.path.join('.'),
                    message: e.message
                }))
            });
        }
        // Return default meta if validation fails
        return {
            totalResults: 0,
            totalPages: 0,
            currentPage: 1,
            pageSize: 24
        };
    }
}
/**
 * Validates sort options
 */
export function validateSortOptions(data, platformName) {
    try {
        return z.array(SortOptionSchema).parse(data);
    }
    catch (error) {
        if (error instanceof z.ZodError) {
            console.warn(`[${platformName}] Invalid sort options, using defaults`);
        }
        // Return default sort options if validation fails
        return [
            { id: 'relevance', label: 'Relevance' }
        ];
    }
}
/**
 * Safe product transformation with validation
 * Use this wrapper in mappers to ensure type safety
 */
export function safeTransformProduct(rawProduct, transformer, platformName) {
    try {
        const transformed = transformer(rawProduct);
        return validateProduct(transformed, platformName);
    }
    catch (error) {
        console.error(`[${platformName}] Error transforming product:`, error);
        return null;
    }
}
/**
 * Safe filter transformation with validation
 */
export function safeTransformFilter(rawFilter, transformer, platformName) {
    try {
        const transformed = transformer(rawFilter);
        return validateFilter(transformed, platformName);
    }
    catch (error) {
        console.error(`[${platformName}] Error transforming filter:`, error);
        return null;
    }
}
